import React from 'react';
import { X, Shield, ShieldCheck, Check } from 'lucide-react';

interface RolePermissionsSidebarProps {
    isOpen: boolean;
    onClose: () => void;
    roleName: string;
    permissions: Record<string, string> | string | null | undefined;
}

const RolePermissionsSidebar: React.FC<RolePermissionsSidebarProps> = ({ isOpen, onClose, roleName, permissions }) => {

    const convertStringToOperations = (permissionStr: string): string[] => {
        const reverseMap: Record<string, string> = {
            'c': 'create',
            'r': 'read',
            'u': 'update',
            'd': 'delete'
        };

        return permissionStr.split('').map(char => reverseMap[char]).filter(Boolean);
    };

    let parsedPermissions: Record<string, string> | null = null;
    try {
        if (typeof permissions === "string") {
            parsedPermissions = JSON.parse(permissions);
        } else if (typeof permissions === "object" && permissions !== null) {
            parsedPermissions = permissions;
        }
    } catch {
        parsedPermissions = null;
    }

    const operations = ["create", "read", "update", "delete"];

    return (
        <>
            {/* Backdrop */}
            <div
                className={`fixed inset-0 bg-black/40 backdrop-blur-sm transition-opacity duration-300 z-[60] ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                onClick={onClose}
            />

            {/* Sidebar - slightly widened to comfortably fit 2 cards per row if needed, or keep same width */}
            <div className={`fixed top-0 right-0 h-full w-full sm:w-[40%] md:w-[35%] bg-white dark:bg-[#0f172a] shadow-2xl border-l border-gray-200 dark:border-gray-700/50 transition-transform duration-500 ease-in-out transform z-[70] ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/50">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-500/10 rounded-lg border border-orange-500/20">
                                <ShieldCheck className="text-orange-500" size={20} />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-gray-900 dark:text-white leading-none">{roleName}</h2>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 font-bold uppercase tracking-widest">Role Permissions</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 text-gray-400 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-all cursor-pointer"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-gray-50/50 dark:bg-transparent">
                        {!parsedPermissions || Object.keys(parsedPermissions).length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center">
                                <div className="p-3 bg-gray-100 dark:bg-gray-800/50 rounded-full mb-3 border border-gray-200 dark:border-gray-700/50">
                                    <Shield className="text-gray-400 dark:text-gray-600" size={36} />
                                </div>
                                <h3 className="text-gray-700 dark:text-gray-300 font-semibold italic text-sm">No permissions assigned</h3>
                                <p className="text-gray-500 text-[11px] mt-1.5">This role doesn't have any specific action permissions yet.</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-2.5">
                                {Object.entries(parsedPermissions).map(([category, operationStr]) => {
                                    const currentOps = convertStringToOperations(operationStr);

                                    return (
                                        <div key={category} className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/50 rounded-xl p-3 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-all group flex flex-col shadow-sm">
                                            <div className="flex items-start justify-between mb-3 gap-2">
                                                <h3 className="text-[11px] font-black text-orange-600 dark:text-orange-400 capitalize tracking-wide leading-tight line-clamp-2">
                                                    {category.replace(/_/g, " ")}
                                                </h3>
                                                <div className="px-1.5 py-0.5 bg-orange-500/10 rounded text-[9px] font-black text-orange-600 dark:text-orange-500 uppercase tracking-tighter shrink-0">
                                                    {currentOps.length}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-1.5 mt-auto">
                                                {operations.map((op) => {
                                                    const hasOp = currentOps.includes(op);
                                                    return (
                                                        <div
                                                            key={op}
                                                            className={`flex items-center justify-center gap-1.5 px-1 py-1.5 rounded-lg border transition-all ${hasOp
                                                                ? 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30 text-green-700 dark:text-green-400'
                                                                : 'bg-gray-100/60 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700/30 text-gray-400 dark:text-gray-500 opacity-50'
                                                                }`}
                                                            title={op}
                                                        >
                                                            <div className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center border shrink-0 ${hasOp
                                                                ? 'bg-green-600 border-green-500'
                                                                : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-transparent'
                                                                }`}>
                                                                {hasOp && <Check size={10} className="text-white stroke-[4]" />}
                                                            </div>
                                                            <span className="text-[9px] font-black uppercase tracking-wider truncate">
                                                                {op}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                </div>
            </div>
        </>
    );
}

export default RolePermissionsSidebar;
